import { IconLoader2 } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { AssistantEvidenceCheck } from "../../../domains/gpt/evidenceCheck";
import type { GptSourceRef } from "../../../domains/gpt/transcript";
import { cn } from "../../../lib/utils";
import { EVIDENCE_CHIP_CLASS, VerifiedQuote } from "./EvidenceDetailsPanel";
import { ExplainedChip } from "./InfoPopover";

function sourceTitle(source: GptSourceRef) {
  switch (source.provenance) {
    case "juisci_internal":
    case "pubmed":
    case "web":
      return source.title;
    case "uploaded_document":
      return source.filename;
    default:
      throw new Error(source satisfies never);
  }
}

type CheckedClaim = Extract<
  AssistantEvidenceCheck,
  { status: "ready" }
>["claims"][number];

const CHECKED_STATUS_STYLE = {
  supported: "border-lime-200 bg-lime-50 text-lime-700",
  contested: "border-amber-200 bg-amber-50 text-amber-700",
  not_found: "border-orange-200 bg-orange-50 text-orange-700",
} as const satisfies Record<CheckedClaim["status"], string>;

function checkedStatusLabel(
  status: CheckedClaim["status"],
  t: (key: string) => string
): string {
  switch (status) {
    case "supported":
      return t("Supported");
    case "contested":
      return t("Contested");
    case "not_found":
      return t("Not found in its sources");
    default:
      throw new Error(status satisfies never);
  }
}

function checkedStatusExplanation(
  status: CheckedClaim["status"],
  t: (key: string) => string
): string {
  switch (status) {
    case "supported":
      return t(
        "A quote verified word-for-word in a cited source supports this statement."
      );
    case "contested":
      return t(
        "A quote verified word-for-word in a cited source contradicts this statement."
      );
    case "not_found":
      return t(
        "The sources this statement cites do not support it as written."
      );
    default:
      throw new Error(status satisfies never);
  }
}

// The Evidence tab of a source answer: each statement checked against the sources that its block cites.
export function AssistantEvidenceCheckPanel({
  check,
  sources,
  onOpenSource,
}: {
  check: AssistantEvidenceCheck;
  sources: readonly GptSourceRef[];
  onOpenSource: (source: GptSourceRef, citationNumber: number) => void;
}) {
  const { t } = useTranslation();
  switch (check.status) {
    case "pending":
      return (
        <p className="m-0 flex items-center gap-2 text-sm text-ds-slate-600">
          <IconLoader2 className="size-4 shrink-0 animate-spin" aria-hidden />
          {t(
            "Checking this answer against its sources. This takes less than a minute."
          )}
        </p>
      );
    case "failed":
      return (
        <p className="m-0 text-sm text-ds-slate-600">
          {t("The evidence check failed. The answer itself is unchanged.")}
        </p>
      );
    case "no_claims":
      return (
        <p className="m-0 text-sm text-ds-slate-600">
          {t("No verifiable statement in this answer.")}
        </p>
      );
    case "ready":
      return (
        <div className="flex flex-col gap-4">
          <p className="m-0 text-sm text-ds-slate-600">
            {t("Each statement is checked against the sources it cites.")}
          </p>
          {check.claims.map((claim) => (
            <div
              key={claim.id}
              className="flex flex-col gap-2.5 rounded-2xl border border-ds-slate-100 bg-white p-4"
            >
              <div className="flex flex-wrap items-center gap-1.5">
                <ExplainedChip
                  className={cn(
                    EVIDENCE_CHIP_CLASS,
                    "border",
                    CHECKED_STATUS_STYLE[claim.status]
                  )}
                  label={checkedStatusLabel(claim.status, t)}
                  title={checkedStatusLabel(claim.status, t)}
                >
                  <span className="block">
                    {checkedStatusExplanation(claim.status, t)}
                  </span>
                  <span className="mt-1 block">
                    {t(
                      "The status is computed automatically from the verified quotes, never chosen by the AI."
                    )}
                  </span>
                </ExplainedChip>
              </div>
              <p className="m-0 text-sm/snug font-semibold text-ds-black">
                {claim.text}
              </p>
              {claim.ungroundedNumbers.length > 0 && (
                <p className="m-0 text-xs text-orange-700">
                  {t("Numbers not found in its cited sources: {{numbers}}", {
                    numbers: claim.ungroundedNumbers.join(", "),
                  })}
                </p>
              )}
              {claim.edges.map((edge, index) => {
                const source = sources.at(edge.sourceNumber - 1);
                if (source === undefined)
                  throw new Error(
                    "should not happen: a quote cites no listed source"
                  );
                const title = sourceTitle(source);
                return (
                  <VerifiedQuote
                    key={index}
                    relation={edge.relation}
                    recommendationGrade={edge.recommendationGrade}
                    verbatim={edge.verbatim}
                    source={{
                      number: edge.sourceNumber,
                      title: title === null ? t("Untitled source") : title,
                      onOpen: () => {
                        onOpenSource(source, edge.sourceNumber);
                      },
                    }}
                  />
                );
              })}
            </div>
          ))}
          {check.droppedClaimCount > 0 && (
            <p className="m-0 text-xs text-ds-slate-500">
              {t("{{count}} statements were left out of the check.", {
                count: check.droppedClaimCount,
              })}
            </p>
          )}
        </div>
      );
    default:
      throw new Error(check satisfies never);
  }
}
